import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WalletService from '../../services/wallet.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

const SECTION_TITLE_SX = { fontSize: '0.875rem', fontWeight: 600, px: 2, py: 1.25 };

const PortalWallet = ({ portalUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [topupRequests, setTopupRequests] = useState([]);
  const [pendingTopups, setPendingTopups] = useState(0);

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const formatDateTime = (date) => date ? new Date(date).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const orgId = portalUser?.organizationId || portalUser?.internalOrganizationId;
      if (!orgId) {
        setError('Unable to identify your organization. Please log out and log in again.');
        setLoading(false);
        return;
      }
      const [walletData, txData, topupData] = await Promise.all([
        WalletService.getPortalWallet(),
        WalletService.getAllTransactions(),
        WalletService.getTopupRequests({ organizationId: orgId })
      ]);
      setWallet(walletData);

      // Filter transactions for this wallet and limit to 10
      const walletId = walletData?.id;
      const orgTx = walletId
        ? txData.data.filter(t => t.walletId === walletId).slice(0, 10)
        : txData.data.slice(0, 10);
      setTransactions(orgTx);

      // Limit top-up requests to last 5
      setTopupRequests((topupData.data || []).slice(0, 5));
      setPendingTopups((topupData.data || []).filter((r) => r.status === 'PENDING').length);
    } catch (err) {
      console.error('Failed to fetch wallet data:', err);
      setError('Failed to load wallet data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [portalUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderAiValidation = (ai) => {
    if (!ai) return <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>N/A</Typography>;
    const pct = Math.round((ai.confidence || 0) * 100);
    const color = ai.isValid ? 'success.main' : 'error.main';
    const Icon = ai.isValid ? CheckCircleIcon : CancelIcon;
    return (
      <Box display="inline-flex" alignItems="center" gap={0.5}>
        <Icon sx={{ fontSize: 16, color }} />
        <Typography sx={{ fontSize: '0.8125rem', color }}>{pct}%</Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const approved = Number(wallet?.approvedBalance || 0);
  const unapproved = Number(wallet?.unapprovedBalance || 0);
  const headSx = getBrandedTableHeadSx(theme);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button size="small" onClick={fetchData} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      <ProTableStats
        cards={[
          { label: 'Approved Balance · available for payments', value: WalletService.formatCurrency(approved), color: approved ? 'success.main' : 'text.disabled' },
          { label: 'Unapproved Balance · pending verification', value: WalletService.formatCurrency(unapproved), color: unapproved ? 'warning.main' : 'text.disabled' },
          { label: 'Pending Top-ups', value: pendingTopups, color: pendingTopups ? 'warning.main' : 'text.disabled' },
        ]}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end', mb: 1.5 }}>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchData} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/portal/wallet/topup')}
          sx={{ textTransform: 'none', fontSize: '0.8125rem', ml: 0.5 }}
        >
          Top Up Wallet
        </Button>
      </Box>

      {/* Recent Transactions */}
      <Paper sx={{ mb: 2 }}>
        <Typography sx={SECTION_TITLE_SX}>Recent Transactions</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead sx={headSx}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Balance After</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No transactions yet</TableCell>
                </TableRow>
              ) : transactions.map((tx) => (
                <TableRow key={tx.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(tx.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={WalletService.getTransactionTypeLabel(tx.type)}
                      size="small"
                      color={WalletService.getTransactionTypeColor(tx.type)}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tx.description}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: tx.direction === 'CREDIT' ? 'success.main' : 'error.main', whiteSpace: 'nowrap' }}>
                    {tx.direction === 'CREDIT' ? '+' : '-'} {WalletService.formatCurrency(tx.amount)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, whiteSpace: 'nowrap', color: Number(tx.balanceAfter) ? 'text.primary' : 'text.disabled' }}>
                    {WalletService.formatCurrency(tx.balanceAfter)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Top-Up Request History */}
      <Paper>
        <Typography sx={SECTION_TITLE_SX}>Top-Up Requests</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead sx={headSx}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell align="right">Requested</TableCell>
                <TableCell align="right">Approved</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">AI Validation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topupRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No top-up requests yet</TableCell>
                </TableRow>
              ) : topupRequests.map((req) => (
                <TableRow key={req.id} hover>
                  <TableCell>{formatDate(req.createdAt)}</TableCell>
                  <TableCell>{req.bankName}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{WalletService.formatCurrency(req.requestedAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500, color: req.approvedAmount ? 'success.main' : 'text.disabled' }}>
                    {req.approvedAmount ? WalletService.formatCurrency(req.approvedAmount) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={WalletService.getTopupStatusLabel(req.status)}
                      size="small"
                      color={WalletService.getTopupStatusColor(req.status)}
                    />
                  </TableCell>
                  <TableCell align="center">{renderAiValidation(req.aiValidation)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PortalWallet;
